import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "../ui/SectionCard";
import Button from "../../../../components/Button/Button.jsx";
import Field from "../ui/Field";
import {
  getExpenseItemsByClaim,
  createExpenseItem,
  updateExpenseItem,
  deleteExpenseItem,
} from "../../../../services/expenseServices";
import { FaReceipt, FaPlus, FaSave, FaTrash, FaSyncAlt } from "react-icons/fa";

const TYPE_OPTIONS = [
  { value: "peage", label: "Péage" },
  { value: "repas", label: "Repas" },
  { value: "hebergement", label: "Hébergement" },
  { value: "parking", label: "Parking" },
  { value: "autre", label: "Autre" },
];

const money = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export default function ExpenseItemsCard({
  claimId,
  onEnsureClaim, // callback pour créer/enregistrer la fiche si besoin
  onItemsChange, // ✅ NEW
  onChanged, // ✅ NEW
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [draftItem, setDraftItem] = useState({
    type: "repas",
    date_frais: "",
    montant: "",
    description: "",
  });

  const totalItems = useMemo(() => {
    return items.reduce((acc, it) => acc + money(it.montant), 0).toFixed(2);
  }, [items]);

  const load = async () => {
    if (!claimId) return;
    try {
      setError("");
      setLoading(true);
      const data = await getExpenseItemsByClaim(claimId);
      const arr = Array.isArray(data) ? data : [];
      setItems(arr);
      onItemsChange?.(arr); // ✅ NEW
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les lignes de frais.");
      setItems([]);
      onItemsChange?.([]); // ✅ NEW
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const resetDraft = () => {
    setDraftItem({
      type: "repas",
      date_frais: "",
      montant: "",
      description: "",
    });
    setEditingId(null);
    setFormOpen(false);
  };

  const startEdit = (it) => {
    setDraftItem({
      type: it.type || "repas",
      date_frais: it.date_frais || "",
      montant: String(it.montant ?? ""),
      description: it.description || "",
    });
    setEditingId(it.id);
    setFormOpen(true);
  };

  const submit = async () => {
    if (!claimId) return;

    if (!draftItem.type) return setError("Type requis.");
    if (!String(draftItem.montant || "").trim())
      return setError("Montant requis.");

    try {
      setError("");
      setSaving(true);

      const payload = {
        type: draftItem.type,
        date_frais: draftItem.date_frais || null,
        montant: money(draftItem.montant),
        description: draftItem.description || null,
      };

      if (editingId) {
        await updateExpenseItem(editingId, payload);
      } else {
        await createExpenseItem(claimId, payload);
      }

      await load();
      onChanged?.(); // ✅ NEW (refresh claim/totals)
      resetDraft();
    } catch (e) {
      console.error(e);
      setError("Erreur lors de l’enregistrement de la ligne.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Supprimer cette ligne de frais ?")) return;
    try {
      setSaving(true);
      await deleteExpenseItem(it.id);
      await load();
      onChanged?.(); // ✅ NEW
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      id="frais"
      title="Frais / Indemnités justificatives"
      subtitle="Ajoutez vos frais (péage, repas, hébergement, parking…). Le total frais est calculé sur la somme des lignes."
      icon={<FaReceipt />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={loading ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={load}
            icon={FaSyncAlt}
            disabled={!claimId}
          />
          <Button
            text="Ajouter une ligne"
            variant="primary"
            size="sm"
            onClick={() => {
              setFormOpen(true);
              setEditingId(null);
            }}
            icon={FaPlus}
            disabled={!claimId}
          />
        </div>
      }
    >
      {!claimId ? (
        <div className="rounded-xl p-4 bg-amber-50 text-amber-900 ring-1 ring-amber-200">
          Pour ajouter des frais, il faut d’abord{" "}
          <strong>enregistrer la fiche</strong> (pour obtenir un id).
          <div className="mt-3">
            <button
              type="button"
              className="h-10 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={onEnsureClaim}
            >
              Enregistrer maintenant
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-xl p-3 bg-rose-50 text-rose-800 ring-1 ring-rose-200">
          {error}
        </div>
      ) : null}

      {/* Formulaire ajout/édition */}
      {claimId && formOpen ? (
        <div className="mt-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold text-slate-800">
              {editingId ? `Modifier la ligne #${editingId}` : "Nouvelle ligne"}
            </div>
            <Button
              text="Fermer"
              variant="outline"
              size="sm"
              onClick={resetDraft}
            />
          </div>

          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <label className="block">
              <div className="text-sm font-semibold text-slate-700 mb-1">
                Type *
              </div>
              <select
                value={draftItem.type}
                onChange={(e) =>
                  setDraftItem((p) => ({ ...p, type: e.target.value }))
                }
                className="w-full rounded-xl px-3 py-2 ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:ring-slate-600 dark:text-white"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <Field
              label="Date"
              type="date"
              value={draftItem.date_frais}
              onChange={(v) => setDraftItem((p) => ({ ...p, date_frais: v }))}
            />

            <Field
              label="Montant (€) *"
              type="number"
              value={draftItem.montant}
              onChange={(v) => setDraftItem((p) => ({ ...p, montant: v }))}
              placeholder="Ex : 12.50"
            />

            <Field
              label="Description"
              value={draftItem.description}
              onChange={(v) => setDraftItem((p) => ({ ...p, description: v }))}
              placeholder="Ex : péage A10 / repas midi..."
            />
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              text={saving ? "Enregistrement..." : "Enregistrer"}
              variant="primary"
              onClick={submit}
              disabled={saving}
              icon={FaSave}
            />

            {editingId ? (
              <Button
                text="Supprimer"
                variant="danger"
                onClick={() => remove({ id: editingId })}
                disabled={saving}
                icon={FaTrash}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Liste */}
      {claimId ? (
        <div className="mt-4 rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden">
          <div className="p-3 bg-slate-50 flex items-center justify-between">
            <div className="text-sm font-semibold dark:text-gray-800">
              Total frais (lignes) : {totalItems} €
            </div>
            <div className="text-xs text-slate-500">Fiche #{claimId}</div>
          </div>

          {loading ? (
            <div className="p-4 text-sm text-slate-600 dark:text-gray-800">
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-slate-600 dark:text-gray-800">
              Aucune ligne pour le moment.
            </div>
          ) : (
            <div className="max-h-[420px] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-white ring-1 ring-slate-200">
                  <tr className="text-left">
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((it) => (
                    <tr key={it.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 capitalize dark:text-gray-800">
                        {it.type}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap dark:text-gray-800">
                        {it.date_frais || "—"}
                      </td>
                      <td className="px-3 py-2 dark:text-gray-800">
                        {it.description || "—"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap dark:text-gray-800">
                        {Number(it.montant || 0).toFixed(2)} €
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            text="Modifier"
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(it)}
                          />

                          <button
                            type="button"
                            onClick={() => remove(it)}
                            className="h-9 px-3 rounded-xl bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2"
                          >
                            <FaTrash /> Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </SectionCard>
  );
}
